<template>
  <TimeoutModal :is-active="step != 'login' && isKiosk" />
  <v-linear-progress color="primary" class="mb-4" />
  <v-tabs-window v-model="step">
    <v-tabs-window-item value="login">
      <v-card class="bg-background">
        <v-card-text class="">
          <div style="text-align: center">
            <img src="@/assets/logo.svg" height="75" />
          </div>
        </v-card-text>
        <LoginForm
          v-if="step == 'login'"
          create-by-default
          @submit="handleLogin"
        />
      </v-card>
    </v-tabs-window-item>
    <v-tabs-window-item value="edit-person">
      <MemberProfileForm
        v-if="step === 'edit-person' && selfId"
        :key="selfId"
        :person-id="selfId"
        :registration="authStore.isProtoMember"
        :person-type="PersonTypeKey.ADULT_GUEST"
        under-age-message="See front desk to register junior guest."
        submit-label="To Agreements"
        @saved="toAgreements"
        @cancel="toLogin"
        tabbed
      />
    </v-tabs-window-item>
    <v-tabs-window-item value="agreements">
      <v-card class="bg-background">
        <v-card-title>Agreements</v-card-title>
        <v-card-text>
          <AgreementsForm
            @terms-agreed-updated="agreementUpdated"
            :agreement-ids="requiredAgreementIds ?? []"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="toEditPerson" :disabled="false">Back</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="toConfirmation"
            :disabled="!agreementsAgreed"
            >Complete Registration</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-tabs-window-item>
    <v-tabs-window-item value="confirmation">
      <v-card class="bg-background">
        <v-card-title>Registration Complete!</v-card-title>
        <v-card-text>
          Thank you for registering with Community Boating.
        </v-card-text>
        <v-btn :to="{ name: 'home' }" variant="text">
          To portal (purchase membership, class signups, etc.)
        </v-btn>
      </v-card>
    </v-tabs-window-item>
  </v-tabs-window>
</template>
<script setup lang="ts">
import { CreateGuestCardDTO, useMemberApi } from "@/autogen";
import MemberProfileForm from "@/components/portal/MemberProfileForm.vue";
import { PersonTypeKey } from "../../../cbi-generator/generated/dto/PersonTypeKey";
import { useMemberPersonId } from "@/composables/people/useMemberPersonId";
import { useAuthStore } from "@/stores/auth";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
const router = useRouter();
const selfId = useMemberPersonId();
const authStore = useAuthStore();
type StepType = "login" | "edit-person" | "agreements" | "confirmation";
const step = ref<StepType>("login");

const route = useRoute();

const isKiosk = computed(() => route.query.isKiosk == "true");

const requiredAgreementIds = computed(() => {
  if (typeof route.query.requiredAgreement == "string")
    return [parseInt(route.query.requiredAgreement)].filter(
      (id) => !Number.isNaN(id),
    );
  return route.query.requiredAgreement
    ?.filter((a) => !!a)
    ?.map((a) => parseInt(a!!))
    .filter((id) => !Number.isNaN(id));
});

const skipCard = computed(() => route.query.skipCard == "true");

const timeoutTimer = ref<NodeJS.Timeout>();

const timeoutConfirmModal = ref(false);

const resetModalDuration = ref(0);

const resetModalSubtitle = computed(
  () =>
    "The registration will reset in " +
    resetModalDuration.value +
    " second" +
    (resetModalDuration.value > 1 ? "s" : ""),
);

const agreementsAgreed = ref(false);

function agreementUpdated(status: boolean) {
  agreementsAgreed.value = status;
}

const timeoutReset = () => {
  timeoutConfirmModal.value = false;
  resetModalDuration.value = modalDurationSeconds;
  resetTimer();
};

watch(timeoutConfirmModal, () => {
  if (!timeoutConfirmModal.value) timeoutReset();
});

const processResetModalTimer = () => {
  if (resetModalDuration.value > 0) {
    setTimeout(processResetModalTimer, 1000);
    resetModalDuration.value = resetModalDuration.value - 1;
  } else {
    if (timeoutConfirmModal.value) timeoutConfirm();
  }
};

const timeoutConfirm = () => {
  toLogin();
  timeoutConfirmModal.value = false;
};

const handleTimeout = () => {
  if (timeoutConfirmModal.value == true || !isKiosk.value) return;
  timeoutConfirmModal.value = true;
  resetModalDuration.value = modalDurationSeconds;
  processResetModalTimer();
};

const timeoutDuration = 60000;

const modalDurationSeconds = 10;

const clearTimer = () => {
  if (timeoutTimer.value) {
    clearTimeout(timeoutTimer.value);
    timeoutTimer.value = undefined;
  }
};

const resetTimer = () => {
  clearTimer();
  if (step.value != "login")
    timeoutTimer.value = setTimeout(handleTimeout, timeoutDuration);
};

onMounted(() => {
  document.addEventListener("mouseup", (e) => {
    resetTimer();
  });

  document.addEventListener("mousemove", (e) => {
    resetTimer();
  });

  // Keyboard events
  document.addEventListener("keydown", (e) => {
    resetTimer();
  });

  document.addEventListener("keyup", (e) => {
    resetTimer();
  });
});

watch(step, () => {
  if (step.value == "login") clearTimer();
  else resetTimer();
});

authStore.watchLoggedIn((loggedIn) => {
  if (loggedIn) {
    toEditPerson();
  } else {
    step.value = "login";
  }
});
function toEditPerson() {
  step.value = "edit-person";
}
const cardLoading = ref(false);
const createdCard = ref<CreateGuestCardDTO>();
async function createGuestCard() {
  cardLoading.value = true;
  const results = await useMemberApi().memberCreateGuestCardPost(
    true,
    isKiosk.value,
  );
  cardLoading.value = false;
  createdCard.value = results.data;
}
async function toConfirmation() {
  step.value = "confirmation";
  if (!skipCard.value) {
    if (!createdCard.value) await createGuestCard();
    router.push({
      name: "Person Card",
      params: { number: createdCard.value?.cardNum },
      query: { isKiosk: isKiosk.value ? "true" : undefined },
    });
  }
}
function toAgreements() {
  step.value = "agreements";
}
function toLogin() {
  authStore.logout();
  step.value = "login";
}
function handleLogin() {
  toEditPerson();
}
</script>
